import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"
import { getI18nMessage } from "../lib/i18n_helper"
import { NativeBridge } from "../lib/native_bridge"
import { whenTabShown } from "../lib/overlay"

// GPS Location Controller - Handles geolocation requests with modal UX
const log = createLogger('GPS')

export default class extends Controller {
  static targets = ["button", "overlay", "errorContainer", "errorText", "modalContent"]
  static values = { autoShow: Boolean }

  connect() {
    this.csrfToken = document.querySelector('[name="csrf-token"]')?.content

    // Store previous focus for restoration
    this.previousActiveElement = document.activeElement

    // Guard flag to prevent re-entrancy during close animation
    this.isClosing = false

    // Setup focus trap
    this.setupFocusTrap()

    this.boundOpen = (event) => this.show(event?.detail?.sourceEvent || event)
    this.element.addEventListener('gps:open', this.boundOpen)

    // Listen for global gps:open events (from NativeBridge after login)
    this.boundWindowOpen = (event) => {
      if (event.detail?.autoShow) {
        log.info('Received gps:open from NativeBridge')
        this.show()
      }
    }
    window.addEventListener('gps:open', this.boundWindowOpen)

    // Auto-show if flag is set (once this tab is on screen)
    if (this.autoShowValue) {
      this.cancelAutoShow = whenTabShown(() => this.show())
    }

    // ✅ 방어적 코딩: overlay target이 있을 때만 접근
    // Auto-focus primary button only if visible
    if (this.hasButtonTarget && this.hasOverlayTarget && !this.overlayTarget.classList.contains('hidden')) {
      this.buttonTarget.focus()
    }
  }

  disconnect() {
    this.cancelAutoShow?.()
    if (this.boundOpen) {
      this.element.removeEventListener('gps:open', this.boundOpen)
      this.boundOpen = null
    }
    // Remove global window listener (from NativeBridge)
    if (this.boundWindowOpen) {
      window.removeEventListener('gps:open', this.boundWindowOpen)
      this.boundWindowOpen = null
    }
    // Remove focus trap listener to prevent memory leak
    if (this.focusTrapHandler) {
      this.element.removeEventListener('keydown', this.focusTrapHandler)
      this.focusTrapHandler = null
    }

    // Restore focus when modal is removed
    if (this.previousActiveElement && this.previousActiveElement !== document.body) {
      this.previousActiveElement.focus()
    }
  }

  async requestLocation(event) {
    event.preventDefault()

    // Disable button and show loading state
    this.setLoadingState(true)

    // Hide previous errors
    this.hideError()

    // Native app: use NativeBridge.GPS for proper permission dialogs
    if (NativeBridge.isNative()) {
      log.info('Using NativeBridge.GPS for location request')
      try {
        const location = await NativeBridge.GPS.requestLocation({ accuracy: "high" })

        // NaN 좌표 방어 체크
        if (Number.isNaN(location.latitude) || Number.isNaN(location.longitude)) {
          throw new Error("Invalid coordinates received from GPS")
        }

        // Haptic: GPS lock confirmed — reassurance feedback
        NativeBridge.Haptic.impact("medium")

        // Convert to GeolocationPosition-compatible format
        this.handleSuccess({
          coords: {
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy
          }
        })
      } catch (error) {
        this.handleNativeGPSError(error)
        this.resetButton()
      }
      return
    }

    // Web browser: Check for geolocation support
    if (!('geolocation' in navigator)) {
      const msg = getI18nMessage(this.element, 'errorsNotSupported', '您的瀏覽器不支援定位功能')
      this.showError(msg, false)
      this.resetButton()
      return
    }

    // Request geolocation
    navigator.geolocation.getCurrentPosition(
      (position) => this.handleSuccess(position),
      (error) => this.handleGeolocationError(error),
      {
        enableHighAccuracy: true,
        timeout: window.BusinessConstants.UITiming.GPS_TIMEOUT_MS,
        maximumAge: 0
      }
    )
  }

  /**
   * Set loading state on button
   * @param {boolean} isLoading
   */
  setLoadingState(isLoading) {
    if (!this.hasButtonTarget) return

    if (isLoading) {
      this.buttonTarget.disabled = true
      this.buttonTarget.classList.add('opacity-50', 'cursor-not-allowed')

      // Show loading spinner and text
      const loadingText = getI18nMessage(this.element, 'loadingText', '定位中...')
      this.buttonTarget.innerHTML = `
        <svg class="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>${loadingText}</span>
      `
    }
  }

  /**
   * Handle NativeBridge GPS errors
   * @param {Error} error - Error object with optional code property
   */
  handleNativeGPSError(error) {
    log.error('NativeBridge GPS error', error)

    // error.code is the primary identifier (from native_bridge.js)
    // Message fallback uses exact strings from BridgeResponse.swift
    const errorCode = error.code || ''
    const errorMsg = error.message || ''

    const isPermissionDenied = errorCode === 'PERMISSION_DENIED' ||
                               errorMsg.includes('Permission denied')

    const isGpsDisabled = errorCode === 'GPS_DISABLED' ||
                          errorMsg.includes('GPS is disabled')

    let message
    let showHint = false

    if (isGpsDisabled) {
      message = getI18nMessage(this.element, 'errorsGpsDisabled', 'GPS已停用。請在設定中開啟位置服務。')
      showHint = false  // iOS native alert already guides to settings
    } else if (isPermissionDenied) {
      message = getI18nMessage(this.element, 'errorsPermissionDenied', '位置權限被拒絕。請允許應用程式存取您的位置以探索附近商品。')
      showHint = true
    } else {
      message = getI18nMessage(this.element, 'errorsUnknown', '無法取得位置資訊')
      showHint = false
    }

    this.showError(message, showHint)
  }

  handleSuccess(position) {
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude

    // Send location to server
    // Accept Turbo Stream for automatic header badge update
    fetch('/update_location', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/vnd.turbo-stream.html, application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify({ latitude, longitude })
    })
    .then(async response => {
      // Check if response is Turbo Stream (header badge will auto-update)
      const contentType = response.headers.get('content-type')
      const isTurboStream = contentType && contentType.includes('turbo-stream')

      if (isTurboStream) {
        // Let Turbo handle the stream (auto-updates header badge)
        const text = await response.text()
        Turbo.renderStreamMessage(text)

        // Show success state on modal and auto-close
        // Note: district name will be visible in header badge (updated by Turbo)
        this.showSuccessState(getI18nMessage(this.element, 'successVerified', '已認證'))
      } else {
        // Fallback to JSON response (backward compatibility)
        const data = await response.json()
        if (data.success) {
          const verifiedText = getI18nMessage(this.element, 'successVerified', '已認證')
          this.showSuccessState(`${data.district} ${verifiedText}`)
        } else {
          const msg = getI18nMessage(this.element, 'errorsUpdateFailed', '位置更新失敗，請稍後再試')
          this.showError(msg, true)
          this.resetButton()
        }
      }
    })
    .catch(error => {
      log.error('Location update error', error)
      const msg = getI18nMessage(this.element, 'errorsNetworkError', '位置更新失敗，請檢查網路連線')
      this.showError(msg, true)
      this.resetButton()
    })
  }

  handleGeolocationError(error) {
    log.error('Geolocation error', error)

    let message = getI18nMessage(this.element, 'errorsUnknown', '無法取得位置資訊')
    let showHint = true

    switch(error.code) {
      case error.PERMISSION_DENIED:
        message = getI18nMessage(this.element, 'errorsPermissionDenied', '位置權限被拒絕。請允許瀏覽器存取您的位置以探索附近商品。')
        showHint = true
        break
      case error.POSITION_UNAVAILABLE:
        message = getI18nMessage(this.element, 'errorsPositionUnavailable', '位置資訊無法取得。請確認GPS已開啟。')
        showHint = false
        break
      case error.TIMEOUT:
        message = getI18nMessage(this.element, 'errorsTimeout', '位置請求逾時。請重試。')
        showHint = false
        break
    }

    this.showError(message, showHint)
    this.resetButton()
  }

  showError(message, showHint = false) {
    if (!this.hasErrorContainerTarget || !this.hasErrorTextTarget) return

    // Clear previous error (safe - textContent only)
    this.errorTextTarget.textContent = ''

    // Add error message (safe - textContent)
    const messageNode = document.createTextNode(message)
    this.errorTextTarget.appendChild(messageNode)

    // Add browser settings hint if needed (safe - createElement + textContent)
    if (showHint) {
      const br = document.createElement('br')
      this.errorTextTarget.appendChild(br)

      const hint = document.createElement('span')
      hint.className = 'block mt-2 font-medium text-red-800'
      hint.textContent = getI18nMessage(this.element, 'hintBrowserSettings', '💡 瀏覽器設定 → 隱私權 → 位置權限')
      this.errorTextTarget.appendChild(hint)
    }

    // Show error container
    this.errorContainerTarget.classList.remove('hidden')
  }

  hideError() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.add('hidden')
    }
  }

  resetButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
      this.buttonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  showSuccessState(displayText) {
    if (!this.hasButtonTarget) return

    // Remove loading state
    this.buttonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    this.buttonTarget.disabled = false

    // Update button with checkmark and text
    // displayText can be either:
    // - District name + "已認證" (from JSON response)
    // - Just "已認證" (from Turbo Stream response, district shown in header badge)
    this.buttonTarget.innerHTML = `
      <svg class="h-3.5 w-3.5 animate-scale-in" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
      </svg>
      <span>${displayText}</span>
    `

    // Change to success styling
    this.buttonTarget.classList.remove(
      'bg-butter-500', 'text-white', 'hover:bg-butter-600',
      'bg-white', 'border', 'border-charcoal-200', 'text-charcoal-600', 'hover:bg-charcoal-50',
      'focus:ring-butter-400', 'focus:ring-charcoal-300'
    )
    this.buttonTarget.classList.add(
      'bg-green-500', 'text-white', 'hover:bg-green-600', 'focus:ring-green-400'
    )

    window.dispatchEvent(new CustomEvent("gps:verified"))

    // Auto-close modal after showing success state (consistent with location_verification_controller.js)
    setTimeout(() => {
      this.close()
    }, window.BusinessConstants.UITiming.MODAL_AUTO_CLOSE_MS)
  }

  close(event) {
    if (event) event.preventDefault()

    // Guard: Prevent re-entrancy during animation
    if (this.isClosing) return

    if (!this.hasOverlayTarget) return

    // Set flag to prevent multiple close calls
    this.isClosing = true

    // Start exit animations
    this.overlayTarget.classList.remove('animate-modal-fade-in')
    this.overlayTarget.classList.add('animate-modal-fade-out')

    if (this.hasModalContentTarget) {
      this.modalContentTarget.classList.remove('animate-modal-scale-in')
      this.modalContentTarget.classList.add('animate-modal-scale-out')
    }

    // Wait for animation to complete before hiding
    this.overlayTarget.addEventListener('animationend', () => {
      // Hide popup instead of removing (so it can be shown again)
      this.overlayTarget.classList.add('hidden')
      this.overlayTarget.classList.remove('animate-modal-fade-out')

      if (this.hasModalContentTarget) {
        this.modalContentTarget.classList.remove('animate-modal-scale-out')
      }

      // Reset closing flag
      this.isClosing = false
    }, { once: true })
  }

  handleBackdropClick(event) {
    // Only close if clicking directly on overlay (not inner content)
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  handleEscapeKey(event) {
    // Guard: Prevent re-entrancy during animation
    if (this.isClosing) return

    // Close modal on ESC key
    this.close()
  }

  stopPropagation(event) {
    // Prevent clicks inside modal from bubbling to backdrop
    event.stopPropagation()
  }

  show(event) {
    const triggerEvent = event?.detail?.sourceEvent ?? event
    if (triggerEvent && typeof triggerEvent.preventDefault === "function") {
      triggerEvent.preventDefault()
    }

    // Guard: Check if modal exists on page
    if (!this.hasOverlayTarget) {
      console.warn('[GPS Controller] Modal overlay not found on this page')

      // Redirect to products page where modal is available
      if (window.location.pathname !== '/products') {
        console.info('[GPS Controller] Redirecting to /products for location setup')
        window.location.href = '/products'
      }
      return
    }

    // Already visible - do nothing
    if (!this.overlayTarget.classList.contains('hidden')) {
      console.debug('[GPS Controller] Modal already visible')
      return
    }

    // Remove hidden class and add entrance animations
    this.overlayTarget.classList.remove('hidden')
    this.overlayTarget.classList.add('animate-modal-fade-in')

    if (this.hasModalContentTarget) {
      this.modalContentTarget.classList.add('animate-modal-scale-in')
    }

    // Reset isClosing flag (in case it was closed before)
    this.isClosing = false

    // Setup focus trap and auto-focus
    this.setupFocusTrap()
    if (this.hasButtonTarget) {
      this.buttonTarget.focus()
    }
  }

  setupFocusTrap() {
    const focusableElements = this.element.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )

    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    // Store handler for cleanup in disconnect()
    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return

      if (e.shiftKey && document.activeElement === firstElement) {
        lastElement.focus()
        e.preventDefault()
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        firstElement.focus()
        e.preventDefault()
      }
    }

    // Add listener (will be removed in disconnect())
    this.element.addEventListener('keydown', this.focusTrapHandler)
  }
}
